import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { QuarterSocialPostCard } from './QuarterSocialPostCard.jsx';
import { QuarterSocialUserAvatar } from './QuarterSocialUserAvatar.jsx';
import {
  deleteQuarterSocialPost,
  fetchQuarterSocialJson,
  getQuarterSocialCsrfToken,
  isQuarterSocialSystemPost,
  isQuarterSocialUserPost,
  updateQuarterSocialPost,
} from './quarterSocialShared.js';

function getAuthLinks() {
  if (typeof window === 'undefined') {
    return {
      loginHref: '/accounts/login/',
      registerHref: '/accounts/register/',
    };
  }
  const next = `${window.location.pathname}${window.location.search}${window.location.hash || ''}` || '/';
  return {
    loginHref: `/accounts/login/?next=${encodeURIComponent(next)}`,
    registerHref: `/accounts/register/?next=${encodeURIComponent(next)}`,
  };
}

function QuarterSocialAuthPrompt() {
  const { loginHref, registerHref } = getAuthLinks();
  return (
    <div class="pp-quarter-social__empty">
      <div>Paylaşım yapmak ve yorum bırakmak için oturum açın veya üye olun.</div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '10px' }}>
        <a class="pp-btn-primary" href={loginHref}>Giriş Yap</a>
        <a class="pp-btn-secondary" href={registerHref}>Üye Ol</a>
      </div>
    </div>
  );
}

export function QuarterSocialApp() {
  const bootstrapCache = typeof window !== 'undefined' && window.PP_QI_SOCIAL_CACHE ? window.PP_QI_SOCIAL_CACHE : null;
  const currentUserId = typeof window !== 'undefined' ? String(window.PP_USER_ID || '').trim() : '';
  const isAuthenticated = Boolean(
    typeof window !== 'undefined' && window.PP_USER_AUTHENTICATED,
  );
  const memberType = typeof window !== 'undefined'
    ? String(window.PP_USER_MEMBER_TYPE || '').trim().toLowerCase()
    : '';
  const restrictToUserPosts = !isAuthenticated || memberType === 'individual';
  const [feedContext, setFeedContext] = useState({
    scope: bootstrapCache?.context?.scope || 'global',
    cityId: bootstrapCache?.context?.cityId || '',
    districtId: bootstrapCache?.context?.districtId || '',
    quarterId: bootstrapCache?.context?.quarterId || '',
  });
  const [items, setItems] = useState(Array.isArray(bootstrapCache?.items) ? bootstrapCache.items : []);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [score, setScore] = useState(bootstrapCache?.score || null);
  const [creating, setCreating] = useState(false);
  const [commentByPost, setCommentByPost] = useState({});
  const [commentsByPost, setCommentsByPost] = useState({});
  const [openComments, setOpenComments] = useState({});
  const [replyTarget, setReplyTarget] = useState({});
  const [activeImageByPost, setActiveImageByPost] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentUserName] = useState(() => window.PP_USER_NAME || 'Mahalle');
  const [currentUserAvatar] = useState(() => window.PP_USER_AVATAR || '');
  const [nextCursor, setNextCursor] = useState(bootstrapCache?.nextCursor || null);
  const [hasMore, setHasMore] = useState(Boolean(bootstrapCache?.nextCursor));
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeFeedTab, setActiveFeedTab] = useState(restrictToUserPosts ? 'user' : 'all');
  const [editingPostId, setEditingPostId] = useState('');
  const [editStateByPost, setEditStateByPost] = useState({});
  const [savingPostId, setSavingPostId] = useState('');
  const [deletingPostId, setDeletingPostId] = useState('');
  const loadMoreRef = useRef(null);

  const quarterId = feedContext.quarterId || '';
  const cityId = feedContext.cityId || '';
  const districtId = feedContext.districtId || '';
  const scope = feedContext.scope || 'global';

  function persistFeedCache(context, payload) {
    try {
      sessionStorage.setItem('pp.qi.v2.socialCache', JSON.stringify({
        context,
        items: payload?.items || [],
        score: payload?.score || null,
        nextCursor: payload?.nextCursor || null,
      }));
    } catch (_) { /* noop */ }
  }

  function emitScoreUpdate(scoreData, targetQuarterId = quarterId) {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent('pp-quarter-social-score:update', {
      detail: {
        quarterId: String(targetQuarterId || '').trim(),
        score: scoreData || null,
      },
    }));
  }

  useEffect(() => {
    function handleContextUpdate(event) {
      const detail = event?.detail || {};
      const nextScope = String(detail.scope || 'global').trim() || 'global';
      setFeedContext({
        scope: nextScope,
        cityId: String(detail.cityId || '').trim(),
        districtId: String(detail.districtId || '').trim(),
        quarterId: String(detail.quarterId || '').trim(),
      });
    }

    function handleContextReset() {
      if (bootstrapCache?.context) {
        setFeedContext({
          scope: bootstrapCache.context.scope || 'global',
          cityId: bootstrapCache.context.cityId || '',
          districtId: bootstrapCache.context.districtId || '',
          quarterId: bootstrapCache.context.quarterId || '',
        });
        return;
      }
      setFeedContext({ scope: 'global', cityId: '', districtId: '', quarterId: '' });
    }

    window.addEventListener('pp-quarter-feed-context:update', handleContextUpdate);
    window.addEventListener('pp-quarter-feed-context:reset', handleContextReset);
    handleContextReset();

    return () => {
      window.removeEventListener('pp-quarter-feed-context:update', handleContextUpdate);
      window.removeEventListener('pp-quarter-feed-context:reset', handleContextReset);
    };
  }, []);

  const canPost = useMemo(
    () => isAuthenticated && scope === 'quarter' && quarterId && text.trim().length > 0,
    [isAuthenticated, scope, quarterId, text],
  );
  const visibleItems = useMemo(() => {
    if (activeFeedTab === 'user') {
      return items.filter((item) => !isQuarterSocialSystemPost(item?.post_type));
    }
    return items;
  }, [items, activeFeedTab]);

  useEffect(() => {
    if (restrictToUserPosts && activeFeedTab !== 'user') {
      setActiveFeedTab('user');
    }
  }, [restrictToUserPosts, activeFeedTab]);

  const renderedItems = useMemo(() => visibleItems.map((item) => ({
    ...item,
    can_manage: Boolean(
      item?.can_manage
      || (
        currentUserId
        && isQuarterSocialUserPost(item?.post_type)
        && String(item?.user_id || '').trim() === currentUserId
      )
    ),
  })), [visibleItems, currentUserId]);

  async function loadPosts({ reset = false, cursor = null } = {}) {
    if (reset) {
      setNextCursor(null);
      setHasMore(false);
      setCommentByPost({});
      setCommentsByPost({});
      setOpenComments({});
      setReplyTarget({});
      setEditingPostId('');
      setEditStateByPost({});
    }
    try {
      const params = new URLSearchParams();
      params.set('scope', scope);
      const pageSize = cursor ? 10 : (scope === 'global' ? 5 : 10);
      params.set('page_size', String(pageSize));
      if (cityId) params.set('city_id', cityId);
      if (districtId) params.set('district_id', districtId);
      if (quarterId) params.set('quarter_id', quarterId);
      if (cursor) params.set('cursor', cursor);

      const postsPromise = fetchQuarterSocialJson(`/api/quarter-social/posts/?${params.toString()}`);
      const scorePromise = scope === 'quarter' && quarterId
        ? fetchQuarterSocialJson(`/api/quarter-social/score/?quarter_id=${encodeURIComponent(quarterId)}`)
        : Promise.resolve(null);
      const [postsRes, scoreRes] = await Promise.all([postsPromise, scorePromise]);
      const nextItems = Array.isArray(postsRes.items) ? postsRes.items : [];
      const mergedItems = reset ? nextItems : items.concat(nextItems);
      setItems(mergedItems);
      setActiveImageByPost((cur) => {
        const next = { ...cur };
        nextItems.forEach((post) => {
          const pid = post?.post_id;
          if (pid && next[pid] == null) next[pid] = 0;
        });
        return next;
      });
      setNextCursor(postsRes?.next_cursor || null);
      setHasMore(Boolean(postsRes?.next_cursor));
      setScore(scoreRes);
      persistFeedCache(
        { scope, cityId, districtId, quarterId },
        {
          items: mergedItems,
          score: scoreRes,
          nextCursor: postsRes?.next_cursor || null,
        },
      );
      emitScoreUpdate(scoreRes, scope === 'quarter' ? quarterId : '');
      setError('');
    } catch (e) {
      setError(e.message || 'Gönderiler yüklenemedi.');
    }
  }

  function changeActiveImage(postId, delta, total) {
    setActiveImageByPost((cur) => {
      const current = Number(cur[postId] || 0);
      const nextIndex = (current + delta + total) % total;
      return { ...cur, [postId]: nextIndex };
    });
  }

  function setActiveImage(postId, index) {
    setActiveImageByPost((cur) => ({ ...cur, [postId]: index }));
  }

  useEffect(() => {
    loadPosts({ reset: true, cursor: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, cityId, districtId, quarterId]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry?.isIntersecting || loadingMore || !nextCursor) return;
      setLoadingMore(true);
      loadPosts({ reset: false, cursor: nextCursor }).finally(() => {
        setLoadingMore(false);
      });
    }, { rootMargin: '200px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, nextCursor, loadingMore]);

  useEffect(() => {
    const handleRefresh = (event) => {
      const detail = event?.detail || {};
      const targetQuarterId = String(detail.quarterId || '').trim();
      if (scope === 'quarter' && quarterId && targetQuarterId && targetQuarterId !== quarterId) return;
      loadPosts({ reset: true, cursor: null });
    };
    window.addEventListener('pp-quarter-social:refresh', handleRefresh);
    return () => {
      window.removeEventListener('pp-quarter-social:refresh', handleRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quarterId]);

  async function submitPost(ev) {
    ev.preventDefault();
    if (!isAuthenticated) {
      setError('Paylaşım yapmak için giriş yapmalısınız.');
      return;
    }
    if (!canPost || creating) return;
    setCreating(true);
    setError('');
    try {
      const fd = new FormData();
      fd.set('quarter_id', quarterId);
      fd.set('city_id', cityId);
      fd.set('district_id', districtId);
      fd.set('text', text.trim());
      selectedFiles.forEach((f) => fd.append('images', f));

      await fetchQuarterSocialJson('/api/quarter-social/posts/create/', {
        method: 'POST',
        headers: { 'X-CSRFToken': getQuarterSocialCsrfToken() },
        body: fd,
      });
      setText('');
      setSelectedFiles([]);
      await loadPosts({ reset: true, cursor: null });
    } catch (e) {
      setError(e.message || 'Gönderi oluşturulamadı.');
    } finally {
      setCreating(false);
    }
  }

  function handleFileChange(ev) {
    const files = ev?.currentTarget?.files ? Array.from(ev.currentTarget.files) : [];
    if (files.length > 6) {
      setError('En fazla 6 görsel yükleyebilirsiniz.');
      setSelectedFiles(files.slice(0, 6));
      return;
    }
    setError('');
    setSelectedFiles(files);
  }

  async function toggleLike(postId) {
    if (!isAuthenticated) {
      setError('Beğeni bırakmak için giriş yapmalısınız.');
      return;
    }
    try {
      await fetchQuarterSocialJson('/api/quarter-social/likes/toggle/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getQuarterSocialCsrfToken() },
        body: JSON.stringify({ target_type: 'post', target_id: postId }),
      });
      await loadPosts({ reset: true, cursor: null });
    } catch (e) {
      setError(e.message || 'Beğeni işlemi başarısız.');
    }
  }

  async function loadComments(postId) {
    try {
      const data = await fetchQuarterSocialJson(`/api/quarter-social/comments/?post_id=${encodeURIComponent(postId)}&page_size=200`);
      setCommentsByPost((cur) => ({ ...cur, [postId]: Array.isArray(data.items) ? data.items : [] }));
    } catch (e) {
      setError(e.message || 'Yorumlar yüklenemedi.');
    }
  }

  async function submitComment(postId) {
    if (!isAuthenticated) {
      setError('Yorum yapmak için giriş yapmalısınız.');
      return;
    }
    const textValue = (commentByPost[postId] || '').trim();
    if (!textValue) return;
    try {
      await fetchQuarterSocialJson('/api/quarter-social/comments/create/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getQuarterSocialCsrfToken() },
        body: JSON.stringify({
          post_id: postId,
          parent_comment_id: replyTarget[postId] || '',
          text: textValue,
        }),
      });
      setCommentByPost((cur) => ({ ...cur, [postId]: '' }));
      setReplyTarget((cur) => ({ ...cur, [postId]: '' }));
      await Promise.all([loadComments(postId), loadPosts({ reset: true, cursor: null })]);
    } catch (e) {
      setError(e.message || 'Yorum gönderilemedi.');
    }
  }

  async function toggleCommentLike(commentId, postId) {
    if (!isAuthenticated) {
      setError('Yorum beğenmek için giriş yapmalısınız.');
      return;
    }
    try {
      await fetchQuarterSocialJson('/api/quarter-social/likes/toggle/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getQuarterSocialCsrfToken() },
        body: JSON.stringify({ target_type: 'comment', target_id: commentId }),
      });
      await loadComments(postId);
    } catch (e) {
      setError(e.message || 'Yorum beğenisi güncellenemedi.');
    }
  }

  function startEditPost(post) {
    const postId = String(post?.post_id || '').trim();
    if (!postId) return;
    setEditingPostId(postId);
    setEditStateByPost((current) => ({
      ...current,
      [postId]: {
        text: String(post?.text || ''),
        images: Array.isArray(post?.images) ? post.images : [],
        keptImageIndexes: Array.isArray(post?.images) ? post.images.map((_, index) => index) : [],
        newFiles: [],
      },
    }));
  }

  function cancelEditPost(postId) {
    setEditingPostId((current) => (current === postId ? '' : current));
    setEditStateByPost((current) => {
      const next = { ...current };
      delete next[postId];
      return next;
    });
  }

  async function saveEditedPost(postId) {
    const editState = editStateByPost[postId] || {};
    const textValue = String(editState.text || '').trim();
    if (!textValue || savingPostId) return;
    setSavingPostId(postId);
    setError('');
    try {
      await updateQuarterSocialPost(postId, {
        text: textValue,
        keepImageIndexes: editState.keptImageIndexes || [],
        newFiles: editState.newFiles || [],
      });
      cancelEditPost(postId);
      await loadPosts({ reset: true, cursor: null });
    } catch (e) {
      setError(e.message || 'Paylaşım güncellenemedi.');
    } finally {
      setSavingPostId('');
    }
  }

  async function deletePost(post) {
    const postId = String(post?.post_id || '').trim();
    if (!postId || deletingPostId) return;
    if (!window.confirm('Bu paylaşımı silmek istediğinize emin misiniz?')) return;
    setDeletingPostId(postId);
    setError('');
    try {
      await deleteQuarterSocialPost(postId);
      if (editingPostId === postId) cancelEditPost(postId);
      await loadPosts({ reset: true, cursor: null });
    } catch (e) {
      setError(e.message || 'Paylaşım silinemedi.');
    } finally {
      setDeletingPostId('');
    }
  }

  return (
    <section class="pp-quarter-social">
      <div class="pp-quarter-social__header">
        <h3>Mahalle Sosyal Paylaşımları</h3>
        <button type="button" class="pp-btn-secondary" onClick={loadPosts}>Yenile</button>
      </div>

      {score?.success ? (
        <div class="pp-quarter-social__score">
          <span>Sistem Skoru: <strong>{score.quarter_system_score}</strong></span>
          <span>Sosyal Skor: <strong>{score.social_score}</strong></span>
          <span>Nihai Skor: <strong>{score.final_score}</strong></span>
        </div>
      ) : null}

      <div class="pp-quarter-social__tabs" role="tablist" aria-label="Sosyal paylaşım filtreleri">
        {!restrictToUserPosts ? (
          <button
            type="button"
            role="tab"
            aria-selected={activeFeedTab === 'all'}
            class={`pp-quarter-social__tab${activeFeedTab === 'all' ? ' is-active' : ''}`}
            onClick={() => setActiveFeedTab('all')}
          >
            Tüm Paylaşımlar
          </button>
        ) : null}
        <button
          type="button"
          role="tab"
          aria-selected={activeFeedTab === 'user'}
          class={`pp-quarter-social__tab${activeFeedTab === 'user' ? ' is-active' : ''}`}
          onClick={() => setActiveFeedTab('user')}
        >
          Kullanıcı Paylaşımları
        </button>
      </div>

      <form class="pp-quarter-social__composer" onSubmit={submitPost}>
        <div class="pp-quarter-social__composer-head">
          <QuarterSocialUserAvatar className="is-composer" name={currentUserName} avatarUrl={currentUserAvatar} />
          <div class="pp-quarter-social__composer-copy">
            <strong>Mahallede neler oluyor?</strong>
            <span>
              {!isAuthenticated
                ? 'Paylaşım ve yorum için önce giriş yapın veya üye olun.'
                : quarterId
                  ? 'Görüşünü yaz, fotoğraf ekle ve komşularınla paylaş.'
                  : 'Önce mahalle seçerek paylaşımı başlat.'}
            </span>
          </div>
        </div>
        {!isAuthenticated ? <QuarterSocialAuthPrompt /> : null}
        <textarea
          value={text}
          onInput={(e) => setText(e.currentTarget.value)}
          placeholder={
            !isAuthenticated
              ? 'Paylaşım yapmak için giriş yapın'
              : quarterId
                ? 'Mahalle hakkında görüşünü paylaş...'
                : 'Önce mahalle seçin'
          }
          disabled={!quarterId || !isAuthenticated}
          rows={3}
        />
        <div class="pp-quarter-social__composer-actions">
          <div class="pp-quarter-social__composer-upload">
            <label class={`pp-quarter-social__upload-label${(!quarterId || !isAuthenticated) ? ' is-disabled' : ''}`} for="pp-quarter-social-images">
              Resim Yükle
            </label>
            <input id="pp-quarter-social-images" type="file" accept="image/*" multiple disabled={!quarterId || !isAuthenticated} onChange={handleFileChange} />
            <span>{selectedFiles.length ? `${selectedFiles.length} görsel seçildi` : 'En fazla 6 görsel'}</span>
          </div>
          <button type="submit" class="pp-btn-primary" disabled={!canPost || creating}>
            {creating ? 'Paylaşılıyor...' : 'Paylaş'}
          </button>
        </div>
        {selectedFiles.length ? (
          <div class="pp-quarter-social__composer-previews">
            {selectedFiles.map((file, index) => (
              <div key={`${file.name}-${index}`} class="pp-quarter-social__composer-preview">
                <span>{file.name}</span>
              </div>
            ))}
          </div>
        ) : null}
      </form>

      {error ? <div class="pp-quarter-social__error">{error}</div> : null}

      <div class="pp-quarter-social__list">
        {renderedItems.map((post) => (
          <QuarterSocialPostCard
            key={post.post_id}
            post={post}
            activeImageIndex={activeImageByPost[post.post_id] || 0}
            isCommentsOpen={Boolean(openComments[post.post_id])}
            comments={commentsByPost[post.post_id] || []}
            commentValue={commentByPost[post.post_id] || ''}
            replyTargetId={replyTarget[post.post_id] || ''}
            isEditing={editingPostId === post.post_id}
            editValue={editStateByPost[post.post_id]?.text || ''}
            savingEdit={savingPostId === post.post_id}
            deletingPost={deletingPostId === post.post_id}
            editImages={editStateByPost[post.post_id]?.images || []}
            keptImageIndexes={editStateByPost[post.post_id]?.keptImageIndexes || []}
            newFiles={editStateByPost[post.post_id]?.newFiles || []}
            canInteract={isAuthenticated}
            onChangeActiveImage={(delta, total) => changeActiveImage(post.post_id, delta, total)}
            onSetActiveImage={(index) => setActiveImage(post.post_id, index)}
            onToggleLike={toggleLike}
            onToggleComments={async (postId) => {
              const isOpen = !!openComments[postId];
              const nextOpen = !isOpen;
              setOpenComments((cur) => ({ ...cur, [postId]: nextOpen }));
              if (nextOpen) await loadComments(postId);
            }}
            onCommentInput={(postId, value) => setCommentByPost((cur) => ({ ...cur, [postId]: value }))}
            onSubmitComment={submitComment}
            onToggleCommentLike={toggleCommentLike}
            onReplyTargetChange={(postId, commentId) => setReplyTarget((cur) => ({ ...cur, [postId]: commentId }))}
            onStartEdit={startEditPost}
            onCancelEdit={cancelEditPost}
            onEditInput={(postId, value) => setEditStateByPost((cur) => ({
              ...cur,
              [postId]: { ...(cur[postId] || {}), text: value },
            }))}
            onToggleEditImage={(postId, imageIndex) => setEditStateByPost((cur) => {
              const state = cur[postId] || {};
              const currentIndexes = Array.isArray(state.keptImageIndexes) ? state.keptImageIndexes : [];
              const nextIndexes = currentIndexes.includes(imageIndex)
                ? currentIndexes.filter((idx) => idx !== imageIndex)
                : currentIndexes.concat(imageIndex).sort((a, b) => a - b);
              return { ...cur, [postId]: { ...state, keptImageIndexes: nextIndexes } };
            })}
            onEditFilesChange={(postId, files) => setEditStateByPost((cur) => ({
              ...cur,
              [postId]: { ...(cur[postId] || {}), newFiles: files.slice(0, 6) },
            }))}
            onSaveEdit={saveEditedPost}
            onDeletePost={deletePost}
          />
        ))}
        {!renderedItems.length ? (
          <div class="pp-quarter-social__empty">
            {activeFeedTab === 'user'
              ? 'Bu mahalle için henüz kullanıcı paylaşımı yok.'
              : 'Bu mahalle için henüz paylaşım yok.'}
          </div>
        ) : null}
        {hasMore ? <div ref={loadMoreRef} class="pp-quarter-social__empty">{loadingMore ? 'Yükleniyor...' : 'Daha fazla içerik yükleniyor...'}</div> : null}
      </div>
    </section>
  );
}

